/**
 * Copy text to the user's clipboard with a fallback for insecure contexts.
 *
 * `navigator.clipboard.writeText` requires a secure context (https:// or
 * localhost). Many staging hosts serve the docs over plain http://, where
 * `navigator.clipboard` is `undefined` and the call throws. In that case we
 * fall back to a hidden-textarea + `document.execCommand('copy')` approach,
 * which works in every browser since IE11.
 *
 * Returns `true` if the copy succeeded.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof window === "undefined") return false;

  // Preferred path: Clipboard API (secure contexts only)
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the textarea fallback
    }
  }

  // Fallback: hidden textarea + execCommand. Works on http://.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.width = "1px";
    ta.style.height = "1px";
    ta.style.padding = "0";
    ta.style.border = "none";
    ta.style.outline = "none";
    ta.style.boxShadow = "none";
    ta.style.background = "transparent";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus({ preventScroll: true });
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
